/**
 * Boxes Collection
 * Loads in-stock products via the collection JSON API and powers dimension search.
 */
(function () {
  'use strict';

  var configEl = document.getElementById('boxes-collection-config');
  if (!configEl) return;

  var tbody = document.getElementById('boxes-product-tbody');
  if (!tbody) return;

  class BoxesCollection {
    constructor() {
      this.products = [];
      this.allRows = [];
      this.config = JSON.parse(configEl.textContent);
      this.tbody = tbody;
      this.initAddToCart();
      this.initDimensionSearch();
      this.loadAndRenderProducts();
    }

    async fetchAllProducts() {
      var handle = this.config.collectionHandle;
      var page = 1;
      var allProducts = [];

      while (true) {
        var response = await fetch('/collections/' + handle + '/products.json?limit=250&page=' + page);
        if (!response.ok) {
          throw new Error('Failed to fetch collection products');
        }

        var data = await response.json();
        allProducts = allProducts.concat(data.products);

        if (data.products.length < 250) {
          break;
        }

        page++;
      }

      return allProducts;
    }

    getFirstAvailableVariant(product) {
      return product.variants.find(function (variant) {
        return variant.available;
      });
    }

    formatMoney(cents) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      var formatString = this.config.moneyFormat;
      if (!formatString) {
        return '$' + (Number(cents) / 100).toFixed(2);
      }

      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var match = formatString.match(placeholderRegex);

      var formatWithDelimiters = function (number, precision, thousands, decimal) {
        var fixed = (number / 100).toFixed(precision);
        var parts = fixed.split('.');
        var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        var centsPart = parts[1] ? decimal + parts[1] : '';
        return dollars + centsPart;
      };

      var value = '';
      var formatType = match ? match[1] : 'amount';

      switch (formatType) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = formatWithDelimiters(cents, 2, ',', '.');
      }

      return formatString.replace(placeholderRegex, value);
    }

    createProductRow(product, variant) {
      var row = document.createElement('tr');
      row.className = 'product-row';
      row.dataset.productId = String(product.id);
      row.dataset.dimensions = product.title;
      row.dataset.productTitle = product.title;

      var titleCell = document.createElement('td');
      titleCell.textContent = product.title;

      var priceCell = document.createElement('td');
      priceCell.className = 'price';
      priceCell.textContent = this.formatMoney(variant.price);

      var quantityCell = document.createElement('td');
      var quantityInput = document.createElement('input');
      quantityInput.type = 'number';
      quantityInput.className = 'quantity-input';
      quantityInput.min = '1';
      quantityInput.value = '1';
      quantityInput.dataset.variantId = String(variant.id);
      quantityCell.appendChild(quantityInput);

      var actionCell = document.createElement('td');
      var addButton = document.createElement('button');
      addButton.className = 'add-to-cart-btn';
      addButton.dataset.variantId = String(variant.id);
      addButton.textContent = 'Add';
      actionCell.appendChild(addButton);

      row.appendChild(titleCell);
      row.appendChild(priceCell);
      row.appendChild(quantityCell);
      row.appendChild(actionCell);

      return row;
    }

    showTableMessage(message) {
      this.tbody.innerHTML = '';
      var row = document.createElement('tr');
      var cell = document.createElement('td');
      cell.colSpan = 4;
      cell.className = 'boxes-loading-message';
      cell.textContent = message;
      row.appendChild(cell);
      this.tbody.appendChild(row);
    }

    async loadAndRenderProducts() {
      try {
        var allProducts = await this.fetchAllProducts();
        var availableProducts = allProducts.filter(function (product) {
          return product.variants.some(function (variant) {
            return variant.available;
          });
        });

        this.tbody.innerHTML = '';

        if (availableProducts.length === 0) {
          this.showTableMessage('No products in stock.');
          return;
        }

        var self = this;
        availableProducts.forEach(function (product) {
          var variant = self.getFirstAvailableVariant(product);
          if (!variant) return;
          self.tbody.appendChild(self.createProductRow(product, variant));
        });

        this.initProductData();
      } catch (error) {
        console.error('Error loading collection products:', error);
        this.showTableMessage('Unable to load products. Please refresh the page.');
      }
    }

    initProductData() {
      this.products = [];
      var rows = document.querySelectorAll('.product-row');
      this.allRows = Array.from(rows);

      var self = this;
      this.allRows.forEach(function (row, index) {
        var dimensions = row.dataset.dimensions || '';
        var parsedDims = self.parseDimensions(dimensions);

        self.products.push({
          row: row,
          index: index,
          dimensions: parsedDims,
          originalDimensions: dimensions,
          title: row.dataset.productTitle || ''
        });
      });
    }

    parseDimensions(dimensionString) {
      if (!dimensionString) return null;

      var cleaned = dimensionString.toString()
        .replace(/["']/g, '')
        .replace(/in|inch|inches/gi, '')
        .replace(/cm|centimeter|centimeters/gi, '')
        .replace(/\s+/g, ' ')
        .trim();

      var patterns = [
        /(\d+\.?\d*)\s*[x×]\s*(\d+\.?\d*)\s*[x×]\s*(\d+\.?\d*)/i,
        /(\d+\.?\d*)\s*[x×]\s*(\d+\.?\d*)/i
      ];

      for (var i = 0; i < patterns.length; i++) {
        var match = cleaned.match(patterns[i]);
        if (match) {
          var dims = match.slice(1).map(function (d) {
            return parseFloat(d);
          }).filter(function (d) {
            return !isNaN(d);
          });

          if (dims.length >= 3) {
            dims.sort(function (a, b) {
              return b - a;
            });
            return {
              length: dims[0],
              width: dims[1],
              height: dims[2],
              original: dimensionString
            };
          }

          if (dims.length === 2) {
            dims.sort(function (a, b) {
              return b - a;
            });
            return {
              length: dims[0],
              width: dims[1],
              height: null,
              original: dimensionString
            };
          }
        }
      }

      return null;
    }

    initDimensionSearch() {
      var searchBtn = document.getElementById('search-dimensions-btn');
      var clearBtn = document.getElementById('clear-search-btn');
      var lengthInput = document.getElementById('dimension-length');
      var widthInput = document.getElementById('dimension-width');
      var heightInput = document.getElementById('dimension-height');
      var resultsInfo = document.getElementById('search-results-info');

      if (!searchBtn || !clearBtn || !lengthInput || !widthInput || !heightInput) {
        console.error('Dimension search elements not found');
        return;
      }

      var self = this;

      searchBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var length = parseFloat(lengthInput.value);
        var width = parseFloat(widthInput.value);
        var height = parseFloat(heightInput.value);

        if (isNaN(length) || isNaN(width) || isNaN(height)) {
          if (resultsInfo) {
            resultsInfo.textContent = 'Please enter all three dimensions';
            resultsInfo.className = 'search-results-info error';
          }
          return;
        }

        self.searchByDimensions(length, width, height, resultsInfo);
      });

      clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        lengthInput.value = '';
        widthInput.value = '';
        heightInput.value = '';
        if (resultsInfo) {
          resultsInfo.textContent = '';
          resultsInfo.className = 'search-results-info';
        }
        self.showAllProducts();
      });

      [lengthInput, widthInput, heightInput].forEach(function (input) {
        if (!input) return;
        input.addEventListener('keypress', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            searchBtn.click();
          }
        });
      });
    }

    searchByDimensions(searchLength, searchWidth, searchHeight, resultsInfo) {
      var tolerance = 0.05;
      var searchDims = [searchLength, searchWidth, searchHeight].sort(function (a, b) {
        return b - a;
      });

      var matches = this.products.map(function (product) {
        if (!product.dimensions) return null;

        var productDims = [
          product.dimensions.length,
          product.dimensions.width,
          product.dimensions.height
        ].filter(function (d) {
          return d !== null;
        }).sort(function (a, b) {
          return b - a;
        });

        if (productDims.length < 3) return null;

        var isExact = true;
        var isSimilar = true;
        var totalDiff = 0;

        for (var i = 0; i < 3; i++) {
          var diff = Math.abs(productDims[i] - searchDims[i]);
          var percentDiff = diff / searchDims[i];

          if (diff > 0.01) isExact = false;
          if (percentDiff > tolerance) isSimilar = false;

          totalDiff += percentDiff;
        }

        if (isExact || isSimilar) {
          return {
            product: product,
            isExact: isExact,
            totalDiff: totalDiff
          };
        }

        return null;
      }).filter(function (match) {
        return match !== null;
      });

      matches.sort(function (a, b) {
        if (a.isExact && !b.isExact) return -1;
        if (!a.isExact && b.isExact) return 1;
        return a.totalDiff - b.totalDiff;
      });

      this.allRows.forEach(function (row) {
        row.style.display = 'none';
      });

      matches.forEach(function (match) {
        match.product.row.style.display = '';
      });

      if (matches.length === 0) {
        resultsInfo.textContent = 'No matching boxes found';
        resultsInfo.className = 'search-results-info error';
      } else {
        var exactCount = matches.filter(function (m) {
          return m.isExact;
        }).length;
        var message = 'Found ' + matches.length + ' matching box' + (matches.length !== 1 ? 'es' : '');
        if (exactCount > 0) {
          message += ' (' + exactCount + ' exact match' + (exactCount !== 1 ? 'es' : '') + ')';
        }
        resultsInfo.textContent = message;
        resultsInfo.className = 'search-results-info success';
      }
    }

    showAllProducts() {
      this.allRows.forEach(function (row) {
        row.style.display = '';
      });
    }

    initAddToCart() {
      var tableContainer = document.querySelector('.boxes-table-container');
      if (!tableContainer) return;

      tableContainer.addEventListener('click', async function (e) {
        var button = e.target.closest('.add-to-cart-btn');
        if (!button) return;

        e.preventDefault();
        e.stopPropagation();

        var variantId = button.dataset.variantId;
        var row = button.closest('.product-row');
        if (!row) return;

        var quantityInput = row.querySelector('.quantity-input');
        var quantity = parseInt(quantityInput.value, 10) || 1;

        try {
          button.disabled = true;
          button.textContent = 'Adding...';

          var response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: quantity
              }]
            })
          });

          if (!response.ok) {
            var errorData = await response.json().catch(function () {
              return {};
            });
            throw new Error(errorData.description || 'Add to cart failed');
          }

          if (window.cartManager) {
            await window.cartManager.updateCartDisplay();
            window.cartManager.showToast('Product added to cart!', 'success');
          } else {
            window.location.reload();
          }

          button.disabled = false;
          button.textContent = 'Add';
        } catch (error) {
          console.error('Error adding to cart:', error);
          button.textContent = 'Error';
          setTimeout(function () {
            button.disabled = false;
            button.textContent = 'Add';
          }, 2000);
        }
      });
    }
  }

  new BoxesCollection();
})();
